import React, { useState, useEffect } from "react";
import { authService, companyService } from "../../services/api";
import { getAuthItem, setAuthItem, setAuthJson } from "../../utils/authStorage";
import paytiempLogo from "../../assets/paytiemp-logo.png";

export default function LoginPage({ onLogin }) {
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const storedCompanyData = getAuthItem("companyData");
        if (storedCompanyData) {
          setCompanyInfo(JSON.parse(storedCompanyData));
          return;
        }
        const data = await companyService.getCompanyByDomain();
        if (data?.success && data?.data?.company) {
          setCompanyInfo(data.data.company);
        }
      } catch (fetchError) {
        console.error("Failed to fetch company info:", fetchError);
      }
    };

    fetchCompanyInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await authService.login(
        credentials.email,
        credentials.password
      );
      if (response.success) {
        // Backend resolves role automatically (admin preferred if multi-role).
        const resolvedTypeFromResponse = String(
          response.userType || response.user?.userType || ""
        ).toLowerCase();
        const backendUserType =
          resolvedTypeFromResponse === "admin" ||
          resolvedTypeFromResponse === "super_admin" ||
          response.user?.role === "admin" ||
          response.user?.role === "super_admin"
            ? "admin"
            : "employee";

        // Proceed with login using backend-resolved role.
        setAuthItem("isAuthenticated", "true");
        setAuthItem("userType", backendUserType);
        // Re-hydrate PermissionsContext now that auth is marked true.
        window.dispatchEvent(new Event("rbac:updated"));

        // Store user ID for employee profile access
        if (response?.employee?.id) {
          setAuthItem("userId", String(response.employee.id));
        } else if (response?.user?.id) {
          setAuthItem("userId", String(response.user.id));
        }

        // Store company data if available in the response
        if (response.company) {
          setAuthJson("companyData", response.company);
        }

        onLogin(backendUserType);
      } else {
        setError(response.message || "Login failed");
      }
    } catch (error) {
      setError(error.message || "An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordMessage("");
    setForgotPasswordLoading(true);

    try {
      const response = await authService.forgotPassword(
        forgotPasswordEmail
      );
      if (response.success) {
        setForgotPasswordMessage(response.message);
        setForgotPasswordEmail("");
        // Close modal after 3 seconds
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordMessage("");
        }, 3000);
      } else {
        setForgotPasswordMessage(
          response.message || "Failed to send reset email"
        );
      }
    } catch (error) {
      setForgotPasswordMessage(error.message || "An error occurred");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#f3f6fb]">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-[1560px]">
        <div className="hidden w-[58%] border-r border-[#e3e8f2] bg-[#edf3fb] px-16 py-12 lg:block">
          <div className="max-w-[640px]">
            <h1 className="mt-6 text-[48px] font-extrabold leading-[1.04] tracking-[-0.02em] text-[#0f172a]">
              Modern HR
              <br />
              Management
              <br />
              <span className="text-[#2e57d7]">Made Simple</span>
            </h1>
            <p className="mt-5 max-w-[520px] text-lg text-[#64748b]">
              Streamline your workforce operations with intelligent automation and insights.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                ["10K+", "Active Users"],
                ["99.9%", "Uptime"],
                ["24/7", "Support"],
              ].map(([value, label]) => (
                <div key={value} className="rounded-2xl border border-[#dbe3ef] bg-white px-4 py-4 shadow-sm">
                  <div className="text-[30px] font-extrabold leading-none text-[#2e57d7]">{value}</div>
                  <p className="mt-1.5 text-xs font-medium text-[#475569]">{label}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-[20px] border border-[#dbe3ef] bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#2773f5] text-white shadow-sm">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.9}
                        d="M12 4v16m8-8H4m3-5h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-[22px] font-bold leading-tight text-[#0f172a]">Onboarding</h3>
                    <p className="mt-1.5 text-base text-[#64748b]">Start the employee journey</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[20px] border border-[#dbe3ef] bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#7c3aed] text-white shadow-sm">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.9}
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h2m6-14h2a2 2 0 012 2v10a2 2 0 01-2 2h-2M12 8v8m-3-3h6"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-[22px] font-bold leading-tight text-[#0f172a]">Management</h3>
                    <p className="mt-1.5 text-base text-[#64748b]">Control and monitor operations</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[20px] border border-[#dbe3ef] bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0ea77a] text-white shadow-sm">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.9}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-[22px] font-bold leading-tight text-[#0f172a]">Attendance</h3>
                    <p className="mt-1.5 text-base text-[#64748b]">Track and report presence</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[20px] border border-[#dbe3ef] bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#f59e0b] text-white shadow-sm">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.9}
                        d="M17 9V7a5 5 0 00-10 0v2m-2 0h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-8a2 2 0 012-2zm7 4h.01"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-[22px] font-bold leading-tight text-[#0f172a]">Payroll</h3>
                    <p className="mt-1.5 text-base text-[#64748b]">Process and disburse salaries</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[20px] border border-[#dbe3ef] bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#ef4444] text-white shadow-sm">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.9}
                        d="M17 16l4-4m0 0l-4-4m4 4H9m-4 7h4a2 2 0 002-2V7a2 2 0 00-2-2H5"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-[22px] font-bold leading-tight text-[#0f172a]">Offboarding</h3>
                    <p className="mt-1.5 text-base text-[#64748b]">Complete exit process</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-center bg-[#f7f8fc] px-5 py-8 lg:w-[42%]">
          <div className="w-full max-w-[560px]">
            <div className="mb-4 flex justify-center">
              <img src={paytiempLogo} alt="Paytiemp logo" className="h-20 w-auto object-contain" />
            </div>

            <div className="overflow-hidden rounded-2xl border border-[#d9e0ec] bg-white shadow-[0_18px_40px_rgba(27,45,94,0.12)]">
            <div className="bg-gradient-to-r from-[#2773f5] to-[#22b4f3] px-7 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Sign in to continue
                </div>
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">Secure Login</span>
              </div>
              <h2 className="mt-6 text-4xl font-bold">Login</h2>
              <p className="mt-1.5 text-sm text-white/90">
                Use your company email and password.
              </p>
            </div>

            <div className="px-7 py-7">
              {error && (
                <div className="mb-5 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-[#334155]">
                    Email Address <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <svg
                      className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#9aa8c0]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                      />
                    </svg>
                    <input
                      type="email"
                      name="username"
                      autoComplete="username"
                      required
                      value={credentials.email}
                      onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                      className="h-12 w-full rounded-xl border border-[#d7deeb] bg-[#fbfcff] py-3 pl-11 pr-4 text-base text-[#334155] outline-none transition-all placeholder:text-[#b0bdd2] focus:border-[#2e57d7] focus:ring-2 focus:ring-[#2e57d7]/25"
                      placeholder="your.email@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-[#334155]">
                    Password <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <svg
                      className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#9aa8c0]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      autoComplete="current-password"
                      required
                      value={credentials.password}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                      className="h-12 w-full rounded-xl border border-[#d7deeb] bg-[#fbfcff] py-3 pl-11 pr-12 text-base text-[#334155] outline-none transition-all placeholder:text-[#b0bdd2] focus:border-[#2e57d7] focus:ring-2 focus:ring-[#2e57d7]/25"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98a7c0] transition-colors hover:text-[#2e57d7]"
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <label htmlFor="remember-me" className="flex cursor-pointer items-center text-sm text-[#64748b]">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 rounded border-[#c8d3e5] text-[#2e57d7] focus:ring-[#2e57d7]/30"
                    />
                    <span className="ml-2">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm font-semibold text-[#2e57d7] hover:text-[#1f43b8]"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-2 flex h-12 w-full items-center justify-center rounded-xl bg-[#2e57d7] text-base font-bold text-white shadow-[0_8px_20px_rgba(46,87,215,0.28)] transition hover:bg-[#2348bc] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Signing in..." : "Sign In"}
                  {!loading && (
                    <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  )}
                </button>
              </form>
            </div>

              <div className="border-t border-[#e3e8f2] bg-[#f8fafe] px-7 py-4 text-center text-xs text-[#8a99b3]">
                By signing in, you agree to our Terms of Service and Privacy Policy
                {companyInfo?.name ? ` • ${companyInfo.name}` : ""}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-[#2773f5] to-[#22b4f3] px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">Reset Password</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail("");
                    setForgotPasswordMessage("");
                  }}
                  className="text-white/80 hover:text-white"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-6">
              <p className="mb-4 text-sm text-[#64748b]">
                Enter your email and we will send a reset link/code.
              </p>
              {forgotPasswordMessage && (
                <div
                  className={`mb-4 rounded-md border px-3 py-2 text-sm ${
                    forgotPasswordMessage.toLowerCase().includes("success")
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-rose-200 bg-rose-50 text-rose-700"
                  }`}
                >
                  {forgotPasswordMessage}
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <input
                  type="email"
                  name="forgot-password-email"
                  autoComplete="email"
                  required
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="h-11 w-full rounded-xl border border-[#d7deeb] px-3 text-sm outline-none transition-all placeholder:text-[#b0bdd2] focus:border-[#2e57d7] focus:ring-2 focus:ring-[#2e57d7]/25"
                  placeholder="your.email@company.com"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail("");
                      setForgotPasswordMessage("");
                    }}
                    className="h-11 flex-1 rounded-xl border border-[#d7deeb] bg-white text-sm font-semibold text-[#475569] transition hover:bg-[#f8fafe]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="h-11 flex-1 rounded-xl bg-[#2e57d7] text-sm font-semibold text-white transition hover:bg-[#2348bc] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {forgotPasswordLoading ? "Sending..." : "Send Reset"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
